import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, Tag, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import GenericModal from "../../../Components/GenericModal";
import locationIcon from "../../../Assets/Icons/location.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import HighRiskComponent from "../../../Components/HighRiskComponent";
import courseUp from "../../../Assets/Icons/Course Up.svg";
import courseDown from "../../../Assets/Icons/Course Down.svg";
import car from "../../../Assets/Vehicle Images/total vehicle.png";
import { getOfflineVehicles } from "../../../API/User/offlinevehicles";
import MapContainerComponent from "../../../Components/MapContainer";
import { TrendTag } from "../Dashboard";

const OfflineAnalysis = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [offlineVehicles, setOfflineVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentLat, setCurrentLat] = useState(null);
  const [currentLng, setCurrentLng] = useState(null);
  const [highRiskVehicles, setHighRiskVehicles] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [offlineVehiclesChange, setOfflineVehiclesChange] = useState(null);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const callModal = (record) => {
    setSelectedVehicle(record);
    setCurrentLat(record.latitude);
    setCurrentLng(record.longitude);
    setIsOpen(true);
  };

  const handleOk = () => {
    if (
      selectedVehicle?.latitude != null &&
      selectedVehicle?.longitude != null
    ) {
      const coords = `${selectedVehicle.latitude}, ${selectedVehicle.longitude}`;

      navigator.clipboard
        .writeText(coords)
        .then(() => {
          messageApi.success(`Coordinates copied: ${coords}`);
        })
        .catch((err) => {
          messageApi.error("Failed to copy coordinates.");
        });
    } else {
      messageApi.warning("No coordinates available for this vehicle.");
    }
  };
  const handleCancel = () => {
    setIsOpen(false);
  };

  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle_name"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const lowerStatus = status?.toLowerCase();

        if (lowerStatus === "online") {
          return <span className="status-badge online">{status}</span>;
        } else if (lowerStatus === "offline") {
          return <span className="status-badge offline">{status}</span>;
        } else {
          return <span style={{ color: "black" }}>{status}</span>;
        }
      },
      filters: [
        { text: "Online", value: "online" },
        { text: "Offline", value: "offline" },
      ],
      onFilter: (value, record) => record.status.toLowerCase() === value,
    },

    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (_, record) => {
        const fromDate = record.from_date ? new Date(record.from_date) : null;
        const toDate = record.to_date ? new Date(record.to_date) : null;

        const formatDate = (date) =>
          date
            ? date.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            })
            : null;

        const fromText = formatDate(fromDate) || "Null";
        const toText = toDate ? formatDate(toDate) : "Today";

        return (
          <span
            className={`duration-badge ${record.status ? record.status.toLowerCase() : ""}`}
          >
            {`${fromText} - ${toText}`}
          </span>
        );
      },
    },
    {
      title: "Location",
      key: "actions",
      render: (_, record) => (
        <a onClick={() => callModal(record)}>
          <img src={locationIcon} alt="location" />
        </a>
      ),
    },
  ];

  const fetchOfflineVehicleData = async () => {
    try {
      setLoading(true);

      const response = await getOfflineVehicles();

      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data?.data || {};
        const vehicles = data?.vehicles;

        const formatted = vehicles.map((item, index) => ({
          key: index + 1,
          customer_name: item.customer_name ? item.customer_name : "Null",
          vehicle: item.vehicle ? item.vehicle : "Null",
          vehicle_name: item.vehicle_name || "Unknown Vehicle",
          status: item.status ? item.status : "Null",
          from_date: item.from_date || null,
          to_date: item.to_date || null,
          latitude: item.latitude,
          longitude: item.longitude,
        }));

        setOfflineVehicles(formatted);
        setThreshold(data?.threshold);
        setOfflineVehiclesChange(data?.offline_vehicles_change)
        setHighRiskVehicles(data?.high_risk_vehicles)
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (error) {
      messageApi.error("Failed to fetch offline vehicles data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchOfflineVehicleData();
  }, []);

  return (
    <div className="offline-analysis">
      {contextHolder}
      <div className="row">
        <div className="tabs-container">
          <div className="tab-item">
            <div className="tab-title">Offline Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">{offlineVehicles?.length}</div>
                <div className="tab-tag">
                  <TrendTag trend={offlineVehiclesChange} />
                </div>
              </div>
              <div className="right">
                <img src={car} alt="car image" />
              </div>
            </div>
          </div>

          <HighRiskComponent high_risk_vehicles={highRiskVehicles} threshold={threshold} />
        </div>
      </div>

      <div className="table">
        <GenericTable
          title="Offline Vehicles"
          dataSource={offlineVehicles}
          columns={customerColumns}
        />
      </div>
      <div className="modal">
        <GenericModal
          title={selectedVehicle?.vehicle_name ?? "Vehicle Name"}
          open={isOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          // cancelText="Shutdown Vehicle"
          // changed the shut down to close because the data in this table is coming from the api which is fetching data from the db table of offline vehicles and ofline vehicles can't be shutdown
          cancelText="Close"
          okText="Copy Coordinates"
        >
          <MapContainerComponent latitude={currentLat} longitude={currentLng} vehicle_name={selectedVehicle?.vehicle_name} />
        </GenericModal>
      </div>
    </div>
  );
};

export default OfflineAnalysis;
